import React, { useState, useEffect, useMemo } from 'react';
import findKey from 'lodash/findKey';
import { Box, Dialog, DialogContent, DialogTitle, MenuItem } from '@mui/material';
import {NOTIFICATION_TYPE, showNotificationBar} from '../../../actions/error-pages/NotificationBarActions';
import { getUserId, isUserAdmin } from '../../../actions/profile/UserActions';
import { LoadingButton, TextButton } from '../../common/StyledButtons';
import { withRouter } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { updateSiteSubmission } from '../../../actions/internal/SiteActions';
import { SUBMISSION_STATUS, SUBMISSION_STATUS_LOOKUP } from '../../../constants/Statuses';
import { StyledMultilineTextField, StyledRichTextbox, StyledTextField } from '../../common/StyledTextField';
import { StyledDropDown } from '../../common/StyledDropDown';
import StyledSwitch from '../../common/StyledSwitch';
import { getAllTeamMembers } from '../../../selectors/AdminSelectors';
import Colors from '../../../constants/Colors';
import { AddCircleOutline, CalendarMonthOutlined, NotificationsActiveOutlined } from '@mui/icons-material';
import { StyledDatePicker } from '../../common/StyledDatePicker';

const EditRequestModal = ({ open, onClose, onSave, request, match }) => {

  const [ status, setStatus ] = useState();
  const [ notes, setNotes ] = useState();
  const [ assignedTo, setAssignedTo ] = useState();
  const [ values, setValues ] = useState({})
  const [errors, setErrors] = useState([]);
  const [ sendNotification, setSendNotification ] = useState(true);
  const [ isLoading, setIsLoading ] = useState(false);

  const [showInternalNotes, setShowInternalNotes] = useState(false);
  const [showDueDateSection, setShowDueDateSection] = useState(false);
  const [dueDate, setDueDate] = useState(null);
  const [dueDateError, setDueDateError] = useState(false);

  const [showReminderSection, setShowReminderSection] = useState(false);
  const [customReminderDate, setCustomReminderDate] = useState(null);

  const teamMembers = getAllTeamMembers();
  const isAdmin = isUserAdmin();

  useEffect(() => {
    if (!request) return;
    setAssignedTo(request.submission_assigned_to);
    if (request.submission_due_date) {
      setDueDate(request.submission_due_date);
      setShowDueDateSection(true);
    }
    if (request.submission_notes) {
      setNotes(request.submission_notes);
      setShowInternalNotes(true);
    }
    if (request.submission_reminder_date) {
      setCustomReminderDate(request.submission_reminder_date);
      setShowReminderSection(true);
    }

  }, [request])

  const setFieldValue = (id, value) => {
    setValues({ ...values, [id]: value })
  }

  const clearForm = () => {
    setStatus();
    setValues({});
    setErrors([]);
    setSendNotification(true);
    setNotes(undefined);
    setAssignedTo(undefined);
    setShowDueDateSection(false);
    setShowInternalNotes(false);
    setDueDate(undefined);
    setCustomReminderDate(undefined);
    setShowReminderSection(false);
  }

  const handleClose = () => {
    clearForm();
    onClose();
  }

  const validateFields = () => {
    const errors = Object.keys(values).filter(key => !values[key] || values[key].trim() === '');
    if (isAdmin && (showDueDateSection || showReminderSection)  && !assignedTo) errors.push('assignedTo')
    if (isAdmin && showDueDateSection && !dueDate) errors.push('dueDate')
    if (isAdmin && showReminderSection && !customReminderDate) errors.push('customReminderDate');
    return errors;
  }

  const customReminderDateWarning = useMemo(() => {
    if (!customReminderDate) return '';
  
    const selectedDate = customReminderDate.toDate
      ? customReminderDate.toDate()
      : new Date(customReminderDate);
  
    const now = new Date();
  
    if (selectedDate < now) {
      return 'Warning: This reminder date is in the past.';
    }
  
    return '';
  }, [customReminderDate]);

  const handleUpdateRequest = async () => {
    try {
      if (!values && !status) { handleClose(); return; }
      setIsLoading(true)

      // validate payload
      const errors = validateFields();
      if (errors.length > 0) {
        setErrors(errors)
        return;
      }

      // send payload to update request
      const masks = ['submission_fields', 'submission_notes', 'submission_assigned_to', 'submission_due_date', 'submission_reminder_date']
      const payload = {
        submission_fields: { ...request.submission_fields, ...values },
        submission_notes: showInternalNotes ? notes : null,
        submission_assigned_to: assignedTo === 'UNASSIGNED' || !assignedTo ? null : assignedTo,
        submission_due_date: showDueDateSection ? dueDate : null,
        submission_reminder_date: showReminderSection ? customReminderDate : null
      }
      if (status) {
        payload.submission_status = SUBMISSION_STATUS[status]
        masks.push('submission_status')
      }

      const site_id = request.submission_identifiers.site ? request.submission_identifiers.site.site_id : undefined;
      const options = { send_submission_notification: sendNotification };

      await updateSiteSubmission(getUserId(), site_id, request.submission_id, masks, payload, options)
      showNotificationBar(NOTIFICATION_TYPE.SUCCESS, "This request has been updated successfully!");
      clearForm();
      onSave();
    } catch(ex) {
      showNotificationBar(NOTIFICATION_TYPE.ERROR, "An error has occurred while canceling this request. If this continues to happen, please contact us.");
    } finally {
      setIsLoading(false)
    }
  }

  const { submission_status, submission_fields, submission_notes, submission_assigned_to } = request || {}
  const defaultFieldValues = submission_fields || {}

  const hasInternalNotes = !!(notes || submission_notes);
  const shouldShowInternalNotes = showInternalNotes;
  const isDueDateSelected = showDueDateSection || !!dueDate;

  return (
    <>
      <Dialog
          fullWidth
          open={open}
          maxWidth={'md'}
      >
          <DialogTitle>
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
              <Box>
                <h4 style={{ margin: 0, padding: 0 }}>Edit Request</h4>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
              {isAdmin && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: {
                      xs: 'column',
                      md: 'row',
                    },
                    gap: 2,
                    width: '100%',
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <StyledDropDown
                      label="Status"
                      value={status || findKey(SUBMISSION_STATUS, (o) => o === submission_status)}
                      onChange={(e) => setStatus(e.target.value)}
                    >
                      {Object.keys(SUBMISSION_STATUS).map(key => (
                        <MenuItem key={key} value={key}>
                          {SUBMISSION_STATUS_LOOKUP[SUBMISSION_STATUS[key]].label}
                        </MenuItem>
                      ))}
                    </StyledDropDown>
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <StyledDropDown
                      label="Assigned To"
                      value={assignedTo || submission_assigned_to}
                      onChange={(e) => setAssignedTo(e.target.value)}
                      error={errors.includes('assignedTo')}
                      helperText={errors.includes('assignedTo') && 'Assign a teammate for notifications.'}
                    >

                      {teamMembers && teamMembers.map(member => (
                        <MenuItem key={member.user_id} value={member.user_id}>
                          {member.team_member_details.first_name} {member.team_member_details.last_name}
                        </MenuItem>
                      ))}
                    </StyledDropDown>
                  </Box>
                </Box>
              )}

              <StyledTextField
                label="Title"
                onChange={(e) => setFieldValue('title', e.target.value)}
                error={errors.includes('title')}
                value={values['title'] || defaultFieldValues['title'] || ''}
              />

              <StyledMultilineTextField
                label="Description"
                onChange={(e) => setFieldValue('description', e.target.value)}
                error={errors.includes('description')}
                value={values['description'] || defaultFieldValues['description'] || ''}
              />

              {isAdmin && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: {
                      xs: 'column',
                      sm: 'row',
                    },
                    gap: 1.5,
                    alignItems: {
                      xs: 'flex-start',
                      sm: 'center',
                    },
                    marginTop: '8px',
                  }}
                >
                  <TextButton
                  onClick={() => setShowReminderSection(prev => !prev)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '4px',
                    color: showReminderSection || customReminderDate ? Colors.primaryColor : 'inherit',
                    '& svg': {
                      color: 'inherit',
                    },
                  }}
                >
                  <NotificationsActiveOutlined fontSize="small" sx={{ marginRight: '4px' }} />
                  {showReminderSection ? 'Remove Reminder' : 'Add Reminder'}
                </TextButton>
                  <TextButton
                    onClick={() => setShowDueDateSection(!showDueDateSection)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '4px',
                      color: isDueDateSelected ? Colors.primaryColor : 'inherit',
                      '& svg': {
                        color: 'inherit',
                      },
                    }}
                  >
                    <CalendarMonthOutlined fontSize="small" sx={{ marginRight: '4px' }} />
                    {showDueDateSection ? 'Remove Due Date' : 'Set Due Date'}
                  </TextButton>

                  <TextButton
                    onClick={() => setShowInternalNotes(!showInternalNotes)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '4px',
                      color: shouldShowInternalNotes ? Colors.primaryColor : 'inherit',
                      '& svg': {
                        color: 'inherit',
                      },
                    }}
                  >
                    <AddCircleOutline fontSize="small" sx={{ marginRight: '4px' }} />
                    {shouldShowInternalNotes ? 'Remove Internal Notes' : 'Add Internal Notes'}
                  </TextButton>
                </Box>
              )}

              {isAdmin && showReminderSection && (
                <Box
                  mt={2}
                  width="100%"
                  display="flex"
                  flexDirection={{ xs: 'column', sm: 'row' }}
                  gap={2}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                >
                  <Box flex={1} width="100%">
                    <StyledDatePicker
                      fullWidth
                      label="Reminder Date"
                      views={["year", "month", "day", "hours", "minutes"]}
                      value={customReminderDate}
                      onChange={(newDate) => {
                        setCustomReminderDate(newDate);
                        setErrors([]);
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          placeholder: 'Choose a reminder date',
                        },
                      }}
                    />
                    <p style={{ paddingTop: '5px', margin: 0 }}>
                      {errors.includes('customReminderDate') && 'Please select a valid date.'}
                      {customReminderDateWarning && <p>{customReminderDateWarning}</p>}
                    </p>
                    
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      padding: '12px',
                      borderRadius: '8px',
                      backgroundColor: '#f7f9fc',
                      color: 'text.secondary',
                      fontSize: '0.875rem',
                      lineHeight: 1.5,
                      border: '1px solid #e6edf5',
                    }}
                  >
                    <strong>
                      Reminder Notifications:
                    </strong>{' '}
                    An email reminder will be sent to the assigned team member on the selected date.
                  </Box>
                </Box>
              )}

              {isAdmin && showDueDateSection && (
                <Box
                  mt={2}
                  width="100%"
                  display="flex"
                  flexDirection={{ xs: 'column', sm: 'row' }}
                  gap={2}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                >
                  <Box flex={1} width="100%">
                    <StyledDatePicker
                      fullWidth
                      label="Due Date"
                      views={['year', 'month', 'day']}
                      value={dueDate}
                      onChange={(newDate) => {
                        setDueDate(newDate);
                        setDueDateError(false);
                        setErrors([]);
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          placeholder: 'Choose a due date',
                          error: dueDateError,
                          helperText: dueDateError ? 'Please select a due date.' : '',
                        },
                      }}
                    />
                    <p style={{ paddingTop: '5px', margin: 0 }}>{errors.includes('dueDate') && 'Please select a valid date.'}</p>
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      padding: '12px',
                      borderRadius: '8px',
                      backgroundColor: '#f7f9fc',
                      color: 'text.secondary',
                      fontSize: '0.875rem',
                      lineHeight: 1.5,
                    }}
                  >
                    <strong>Due Date Notifications:</strong> An email will be sent to the assigned team member 5 days before the due date and again 24 hours before the due date.
                  </Box>
                </Box>
              )}

              {isAdmin && shouldShowInternalNotes && (
                <Box sx={{ marginTop: '18px' }}>
                  <h4
                    style={{
                      margin: 0,
                      fontSize: '18px',
                      marginBottom: '5px',
                    }}
                  >
                    Internal Notes
                  </h4>

                  <StyledRichTextbox
                    value={notes || submission_notes}
                    onChange={setNotes}
                  />
                </Box>
              )}
            </Box>
          </DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', padding: '16px'}}>
            <Box>
              {status && <StyledSwitch
                  title="Notify Client"
                  onChange={(e) => { setSendNotification(e.target.checked) }}
                  checked={sendNotification}
              />}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end'}}>
              <TextButton sx={{ marginRight: '10px' }} onClick={handleClose} disabled={isLoading}>Cancel</TextButton>
              <LoadingButton onClick={handleUpdateRequest} isLoading={isLoading}>Save</LoadingButton>
            </Box>
          </Box>
      </Dialog>
    </>
  )
}

export default observer(withRouter(EditRequestModal));

/*
  <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', padding: '16px'}}>
    <TextButton sx={{ marginRight: '10px' }} onClick={handleClose} disabled={isLoading}>Cancel</TextButton>
    <LoadingButton onClick={handleUpdateRequest} isLoading={isLoading}>Save</LoadingButton>
  </Box>
*/
